import React, { useEffect, useState } from 'react';
import {
    formatAllCapsLabel,
    formatCentsToDollars,
    formatCurrency,
    formatE164ToPhone,
    formatPhoneToE164,
    formatPriceToCents,
    isValidPriceFormat,
} from '../common/utils';
import { PaymentPlatform } from '../services/payment/types';
import { usePaymentService } from '../services/payment';
import { useUserService } from '../services/user';
import { ResponseStatus } from '../services/common/types';

const CASH_OUT_THRESHOLD = 500;

interface CashOutModalProps {
    availableCash: number;
    onClose: () => void;
    onSubmit: (amount: number) => void;
}

const CashOutModal = ({ availableCash, onClose, onSubmit }: CashOutModalProps) => {
    const userService = useUserService();
    const paymentService = usePaymentService();

    const [platform, setPlatform] = useState<PaymentPlatform>(PaymentPlatform.ZELLE);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [amount, setAmount] = useState(formatCentsToDollars(availableCash));
    const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);

    const canCashOut = availableCash >= CASH_OUT_THRESHOLD;
    const isBusy = isLoadingPreferences || isSubmitting;

    useEffect(() => {
        userService.getUserInfo()
            .then(details => {
                if(details){
                    setPlatform(details.paymentPlatform);
                    setPhoneNumber(formatE164ToPhone(details.phoneNumber));
                }
            })
            .catch(err => console.error('Failed to load payment preferences', err))
            .finally(() => setIsLoadingPreferences(false));
    }, [userService]);

    const handleOverlay = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isBusy) onClose();
    };

    const handleConfirm = () => {
        setError(null);

        const amountCents = formatPriceToCents(amount);
        const isAmountValid = amountCents >= CASH_OUT_THRESHOLD && amountCents <= availableCash;
        const formattedPhone = formatPhoneToE164(phoneNumber);

        if (!formattedPhone) {
            setError('Enter a valid 10-digit phone number');
            return;
        }
        if (!isValidPriceFormat(amount)) {
            setError('Enter an amount with up to 2 decimal places');
            return;
        }
        if (!isAmountValid) {
            setError(amountCents < CASH_OUT_THRESHOLD ? `Enter an amount greater than ${formatCurrency(CASH_OUT_THRESHOLD)}` : 'Amount exceeds available cash');
            return;
        }

        setIsSubmitting(true);
        paymentService.createPaymentRequest({
            paymentPlatform: platform,
            phoneNumber: formattedPhone,
            amount: amountCents,
        })
        .then((response) => {
            if(response.status === ResponseStatus.ERROR) {
                setError(response.message);
                return;
            }
            setIsSuccess(true);
            setTimeout(() => onSubmit(amountCents), 2000);
        })
        .catch((err) => {
            console.error(err.message);
            setError('Something went wrong. Please try again later.');
        })
        .finally(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <div className="modal-overlay open" onClick={handleOverlay}>
            <div className="modal cashout-modal">
                <button className="modal-close" onClick={onClose} disabled={isBusy}>✕</button>

                <div className="modal-title">Cash out</div>

                { isLoadingPreferences ? (
                    <div className="modal-sub">Loading your payment details…</div>
                )  : isSuccess ? (
                    <>
                        <div className="modal-sub">
                            Cash out request submitted!
                        </div>
                        <div className="modal-cta">
                            <button className="btn-ghost" onClick={onClose}>Done</button>
                        </div>
                    </>
                ) : !canCashOut ? (
                    <>
                        <div className="modal-sub">
                            You need at least {formatCurrency(CASH_OUT_THRESHOLD)} in available cash to cash out.
                        </div>
                        <div className="threshold-status">
                            <span className="threshold-status-label">Your balance</span>
                            <span className="threshold-status-value">{formatCurrency(availableCash)}</span>
                        </div>
                        <div className="modal-cta">
                            <button className="btn-ghost" onClick={onClose}>Close</button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="modal-sub">Withdraw your available cash to a payment platform</div>

                        <div className="field-group">
                            <label className="field-label">Payment platform</label>
                            <div className="field-select-wrap">
                                <select
                                    className="field-select"
                                    value={platform}
                                    onChange={e => setPlatform(e.target.value as PaymentPlatform)}
                                    disabled={isBusy}
                                >
                                    {Object.values(PaymentPlatform).map(p => (
                                        <option key={p} value={p}>{formatAllCapsLabel(p)}</option>
                                    ))}
                                </select>
                                <span className="field-select-arrow">▾</span>
                            </div>
                        </div>

                        <div className="field-group">
                            <label className="field-label">Phone number</label>
                            <input
                                type="tel"
                                className="field-input mono"
                                placeholder="(555) 123-4567"
                                value={phoneNumber}
                                onChange={e => setPhoneNumber(e.target.value)}
                                disabled={isBusy}
                            />
                        </div>

                        <div className="field-group">
                            <label className="field-label">Amount</label>
                            <div className="field-amount-wrap">
                                <span className="field-amount-prefix">$</span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    className="field-input field-amount-input"
                                    value={amount}
                                    onChange={e => setAmount(e.target.value)}
                                    disabled={isBusy}
                                />
                            </div>
                            <div className="field-hint">Available to withdraw: {formatCurrency(availableCash)}</div>
                            <div className="field-hint">Minimum cash out: {formatCurrency(CASH_OUT_THRESHOLD)}</div>
                        </div>

                        { error &&
                            <div className="field-error">{error}</div>
                        }

                        <div className="modal-cta">
                            <button className="btn-primary" onClick={handleConfirm} disabled={isBusy}>
                                { isSubmitting ? <span className="btn-spinner" /> : 'Confirm cash out' }
                            </button>
                            <button className="btn-ghost" onClick={onClose} disabled={isBusy}>Cancel</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CashOutModal;